<template>
  <div class="tab-pane active" id="form-step-3">
    <div class="row">
      <div class="col-sm-12">
        <h4 class="info-text ">
          Tell us more about your property plans so we can tailor information
          for you.
        </h4>
      </div>
      <div class="col-sm-12" v-if="!this.countryBuyingIn">
        <div class="form-group">
          <label class="form-label required">Country you are buying in</label>
          <vue-select
            class="vue-select"
            name="countryBuyingIn"
            :options="countries"
            :class="{ error: errors.has('step3.countryBuyingIn') }"
            label="CountryName"
            v-model="countryBuyingIn"
            data-vv-scope="step3"
            v-validate="'required'"
            autocomplete="none"
          >
            <template slot="selected-option" slot-scope="option">
              <div class="flex">
                <div class="long-and-truncated-with-child-corrected">
                  <span>{{ option.CountryName }}</span>
                </div>
              </div>
            </template>
            <template slot="option" slot-scope="option">
              <span
                class="flag-icon flag-icon-squared"
                :class="('flag-icon-' + option.TwoCharCode) | lowerCase"
              ></span
              >&nbsp;
              {{ option.CountryName }}
            </template>
          </vue-select>
        </div>
      </div>
      <div class="col-sm-12">
        <div class="form-group">
          <label class="form-label required"
            >Region/Area you are looking to buy in?</label
          >
          <div class="autocomplete-container position-relative">
            <!-- Input with Bootstrap styling -->
            <div class="input-group">
              <input
                type="text"
                class="form-control"
                placeholder="Search for location..."
                v-model="query"
                @input="handleInput"
                @blur="showSuggestions = false"
                @focus="showSuggestions = filteredRegions.length > 0"
                @keydown.enter.prevent="showSuggestions && filteredRegions.length ? selectRegion(filteredRegions[0]) : null"
                aria-label="Search for location"
                :class="{
                  error: errors.has('step3.region') || regionSelectionError,
                }"
                data-vv-scope="step3"
                v-validate="'required'"
                name="region"
              />
            </div>

            <!-- Dropdown for suggestions -->
            <ul
              v-if="showSuggestions && filteredRegions.length"
              class="suggestion-box"
              @mousedown.prevent
            >
              <li
                v-for="region in filteredRegions"
                :key="region.geoNameId"
                class="autocomplete-item"
                @click="selectRegion(region)"
              >
                <span
                  v-html="
                    highlightMatch(region.displayName) +
                      ' (' +
                      region.levelName +
                      ')'
                  "
                  v-if="region.geoNameId > 0"
                ></span>
                <span v-else>{{ region.displayName }}</span>
                <br />
                <span class="dark-gray">{{ region.parentDisplayName }}</span>
              </li>
            </ul>

            <!-- Error message -->
            <div v-if="errorMessage" class="text-danger mt-2">
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-sm-12">
        <div class="form-group">
          <label class="form-label required">Property budget</label>
          <select
            name="propertyBudget"
            class="form-control custom-select"
            v-model.trim="propertyBudget"
            data-vv-scope="step3"
            v-validate="'required'"
            :class="{ error: errors.has('step3.propertyBudget') }"
            autocomplete="none"
          >
            <option value="" selected="selected">Please select...</option>
            <option
              :value="budget.CodesId"
              v-for="budget in budgetValues"
              :key="budget.CodesId"
              >{{ budget.SelectionValue }}</option
            >
          </select>
        </div>
      </div>
      <div class="col-sm-12">
        <div class="form-group">
          <label class="form-label required">When are you buying?</label>
          <select
            name="timescale"
            class="form-control custom-select"
            v-model.trim="timescale"
            data-vv-scope="step3"
            v-validate="'required'"
            :class="{ error: errors.has('step3.timescale') }"
            autocomplete="none"
          >
            <option value="" selected="selected">Please select...</option>
            <option
              :value="timescale.CodesId"
              v-for="timescale in timeScales"
              :key="timescale.CodesId"
              >{{ timescale.SelectionValue }}</option
            >
          </select>
        </div>
      </div>

      <div class="col-sm-12">
        <div class="form-group">
          <label class="form-label required"
            >How are you funding your purchase?</label
          >
          <select
            name="funding"
            class="form-control custom-select"
            v-model.trim="funding"
            data-vv-scope="step3"
            v-validate="'required'"
            :class="{ error: errors.has('step3.funding') }"
            autocomplete="none"
          >
            <option value="" selected="selected">Please select...</option>
            <option
              :value="option.CodesId"
              v-for="option in fundingOptions"
              :key="option.CodesId"
              >{{ option.FriendlyName }}</option
            >
          </select>
        </div>
      </div>
      <div class="col-sm-12">
        <div class="form-group">
          <label class="form-label">Do you have a viewing trip booked?</label>
          <div class="d-flex flex-row ml-2">
            <div class="form-check mr-3">
              <input
                class="form-check-input"
                type="radio"
                name="flexRadioDefault"
                id="tripPlannedRadioNo"
                :value="false"
                v-model="viewingTripDatePickShown"
                @change="clearSelectedDate"
                checked
              />
              <label class="form-check-label" for="tripPlannedRadioNo">
                No
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="flexRadioDefault"
                id="tripPlannedRadioYes"
                :value="true"
                v-model="viewingTripDatePickShown"
              />
              <label class="form-check-label" for="tripPlannedRadioYes">
                Yes
              </label>
            </div>
          </div>

          <div v-if="viewingTripDatePickShown">
            <label class="form-label mt-2">
              When have you planned a viewing trip to see properties?
            </label>
            <datepicker
              v-model="tripPlannedOn"
              id="plannedOn"
              :format="customFormatter"
              class="form-control"
              :disabled-dates="disabledDates"
              :clear-button="true"
              :calendar-button="true"
              calendar-button-icon="fa fa-calendar"
            ></datepicker>
          </div>
        </div>
      </div>
      <div class="col-sm-12 mt-2">
        <hr />
      </div>
      <div class="col-sm-12">
        <div class="form-group">
          <label class="form-label font-weight-bold"
            >Wherever you are with your overseas property plans, we can
            help.</label
          >
          <label class="form-label mb-0">
            Select the services you're interested in and we'll connect you with
            trusted experts.
          </label>
          <service-selection v-model="opportunityTypes" />
        </div>
      </div>

      <!-- next virtual event opt-in -->
      <template v-if="showVirtualEventOption">
        <div class="col-sm-12">
          <div class="form-group">
            <h4 class="info-text font-weight-bold">
              Join our Virtual Property Event on {{ virtualEventDate }}
            </h4>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="nextVirtualEvent"
                name="nextVirtualEvent"
                v-model="virtualEventOptIn"
              />&nbsp;
              <label for="nextVirtualEvent" class="form-label d-inline"
                >Yes, I'd like to join the Virtual Property Event</label
              >
            </div>
            <div class="mt-3">
              <img
                src="/assets/img/YOH-EVENT_Web@2x.jpg"
                title=""
                class="img-fluid"
              />
            </div>
            <div class="mt-3">
              <div class="input-group d-flex align-items-center">
                <i class="fa fa-calendar fa-lg mr-2 c-orange"></i>

                <p class=" align-items-center mb-0">
                  <strong>Date</strong> {{ virtualEventDate }}
                </p>
              </div>
              <div class="input-group d-flex align-items-center">
                <i class="fa fa-clock-o fa-lg mr-2 c-orange"></i>

                <p class=" align-items-center mb-0">
                  <strong>Time</strong> 8:00 am - 8:00 pm (UK time)
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-12">
          <message-box :type="5" />
        </div>
      </template>
      <div class="col-sm-12 mt-2">
        <privacy-policy />
      </div>
      <div class="col-sm-12" v-if="skipSmartStep">
        <message-info />
      </div>
      <div class="col-sm-12 mt-3" v-if="skipSmartStep">
        <div class="pull-right">
          <input
            type="button"
            class="btn btn-fill btn-success btn-block btn-wd btn-md"
            name="save"
            value="Register"
            @click.prevent="save()"
          />
        </div>
        <div class="pull-left">
          <input
            type="button"
            class="btn btn-previous btn-fill btn-default btn-wd btn-md"
            name="previous"
            value="Previous"
            @click="goToPreviousStep()"
          />
        </div>
      </div>
      <Loading v-if="loading" />
    </div>
  </div>
</template>
<script>
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapFields } from "vuex-map-fields";
import { mapGetters } from "vuex";
import MessageInfo from "@/components/ui/MessageInfo.vue";
import mixin from "@/components/shared/mixinUtility";
import { setDatadogUser, trackYohRegistrationCompleted, trackExistingClientCompleted } from "@/helper/datadog";
import Loading from "@/components/ui/Loading.vue";
import axios from "axios";
import { Common } from "@/helper/common";
import moment from "moment";
import Datepicker from "vuejs-datepicker";
import MessageBox from "@/components/ui/MessageBox.vue";
import PrivacyPolicy from "@/components/ui/PrivacyPolicy.vue";
import _ from "lodash";
import ServiceSelection from "@/components/services/ServiceSelection.vue";

export default {
  name: "Preference",
  mixins: [mixin],
  data() {
    return {
      loading: false,
      showAboutUs: true,
      disabledDates: {
        to: new Date(), // Disable all dates up to specific date
      },
      viewingTripDatePickShown: false,
      query: "",
      regions: [], // Full list of regions fetched from the API
      filteredRegions: [], // Filtered regions to display
      selectedRegion: null,
      showSuggestions: false,
      errorMessage: "",
      regionSelectionError: false,
      debouncedSearch: null,
      usaRegions: [
        {
          geoNameId: 5332921,
          displayName: "California",
          parentGeoNameId: 6252001,
          parentDisplayName: "United States",
          level: 1,
          population: 37691912,
          levelName: "Region",
        },
        {
          geoNameId: 4155751,
          displayName: "Florida",
          parentGeoNameId: 6252001,
          parentDisplayName: "United States",
          level: 1,
          population: 17671452,
          levelName: "Region",
        },
        {
          geoNameId: 5128638,
          displayName: "New York",
          parentGeoNameId: 6252001,
          parentDisplayName: "United States",
          level: 1,
          population: 19274244,
          levelName: "Region",
        },
        {
          geoNameId: 0,
          displayName: "Other US States",
          parentGeoNameId: 0,
          parentDisplayName: "United States",
          level: 1,
          population: 0,
          levelName: "Region",
        },
      ],
    };
  },
  props: {
    prev: Function,
  },
  computed: {
    ...mapFields([
      "form.countryBuyingIn",
      "form.region",
      "form.regionId",
      "form.propertyBudget",
      "form.funding",
      "form.timescale",
      "form.referralSource",
      "form.opportunityTypes",
      "form.tripPlannedOn",
      "form.virtualEventOptIn",
    ]),
    ...mapGetters({
      currentActive: "getCurrentActive",
      tabs: "getTabs",
      formData: "getFormData",
      allCountries: "getCountries",
      timeScales: "getTimeScales",
      budgetValues: "getBudgetValues",
      fundingOptions: "getFundingOptions",
      contactId: "getContactId",
      skipSmartStep: "skipSmartStep",
      actions: "getActions",
    }),
    countries() {
      const webinarCountries = this.formData.webinar.countries;

      let filtered = [];
      webinarCountries.forEach((web) => {
        filtered.push(
          this.allCountries.find((c) => c.CountryId === web),
        );
      });
      return filtered;
    },
    virtualEventDate() {
      const event = this.formData.virtualEventToOptin;
      if (!event || !event.endDate) return "to be announced";
      return moment(event.endDate).format("Do MMMM YYYY");
    },
    showVirtualEventOption() {
      // non US and not virtual event and not the same virtual event as the one selected and virtual event still available
      //&& this.countryBuyingIn.TwoCharCode != "US"
      const showVirtualEvent =
        this.formData.webinar.virtualEvent != true &&
        this.formData.virtualEventToOptin;
      return showVirtualEvent;
    },
  },
  components: {
    "vue-select": VueSelect,
    Loading,
    MessageInfo,
    Datepicker,
    "message-box": MessageBox,
    "privacy-policy": PrivacyPolicy,
    "service-selection": ServiceSelection,
  },
  methods: {
    clearSelectedDate() {
      this.formData.tripPlannedOn = null;
    },
    customFormatter(date) {
      return moment(date).format("Do MMMM YYYY");
    },
    save() {
      //check if existing client with smart account already otherwise logic is in wizard component
      if (this.skipSmartStep) {
        this.resetMessageInfo();
        this.$root.$validator
          .validate("step" + (this.currentActive + 1) + ".*")
          .then(async (valid) => {
            if (valid) {
              try {
                if (!this.validateSelection()) {
                  this.showGenericErrorMessage(
                    "Please select a valid location from the list.",
                    false,
                  );
                  return false;
                }
                this.loading = true;
                const resContact = await this.createContact();
                if (resContact.data.status == 0) {
                  this.showGenericErrorMessage(
                    "We've encountered a problem.",
                    false,
                  );
                  this.loading = false;
                  return false;
                } else {
                  Common.setCookie("email", this.formData.email, 365);
                  Common.setCookie("title", this.formData.title, 365);
                  Common.setCookie(
                    "mobilePrefix",
                    this.formData.mobilePrefix,
                    365,
                  );
                  Common.setCookie("mobile", this.formData.mobile, 365);
                  Common.setCookie("firstname", this.formData.firstName, 365);
                  Common.setCookie("lastname", this.formData.lastName, 365);
                  this.$store.dispatch(
                    "setContactId",
                    resContact.data.contactId,
                  );
                  //set datadog user
                  setDatadogUser(
                    resContact.data.contactId,
                    this.formData.email,
                  );
                  //create enquiry/opportunity - the backend also registers the
                  //contact with webinar-service for the enquiry's event
                  await this.createEnquiryOppurtunity(
                    this.formData.webinar,
                    false,
                  );
                  if (
                    this.formData.virtualEventOptIn &&
                    this.formData.virtualEventToOptin
                  )
                    // auto register to next virtual event
                    this.createEnquiryOppurtunity(
                      this.formData.virtualEventToOptin,
                      true,
                    );

                  await this.$store.dispatch("setShowThankYouForYOH", true);
                  this.goNextStep();
                  this.loading = false;

                  //redirect to thank you page
                  // if (this.formData.webinar.virtualEvent) {
                  //   window.location.href =
                  //     "https://www.youroverseashome.com/thankyou-virtual/";
                  // } else
                  // window.location.href =
                  //   "https://www.youroverseashome.com/thankyou-events/";
                }
              } catch (ex) {
                this.showGenericErrorMessage(
                  "We've encountered a problem.",
                  false,
                );
                this.loading = false;
              }
              //all valid?
            } else {
              //not valid
              this.showGenericErrorMessage(
                "Please enter all mandatory fields.",
                false,
              );
            }
          });
      }
    },
    goToPreviousStep() {
      this.$store.dispatch("setHideNavigation", false);
      this.prev();
    },
    async confirmDetails() {
      const payload = {
        contactId: this.contactId,

        isContactExist: this.formData.isContactExist,
      };

      const { data } = await axios.post(
        process.env.VUE_APP_API_URL + "/client/ConfirmDetails",
        payload,
        { headers: { Authorization: `Bearer ${this.jwtToken}` } },
      );

      return data;
    },
    goNextStep() {
      trackYohRegistrationCompleted();
      trackExistingClientCompleted();
      this.$store.dispatch("setCurrentActive", this.currentActive + 1);
      this.tabs.forEach((tab) => {
        tab.isActive = false;
      });

      this.tabs[this.currentActive].isActive = true;
      Common.scrollBackUp();

      this.resetMessageInfo();
    },
    handleInput() {
      this.errorMessage = ""; // Clear error message

      this.selectedRegion = null;
      this.regionId = "";
      this.region = "";

      if (this.query.length >= 3) {
        this.debouncedSearch();
      } else {
        this.filteredRegions = [];
        this.showSuggestions = false;
      }
    },

    // Fetch regions from API
    async fetchRegions() {
      if (!this.query.trim()) {
        this.filteredRegions = [];
        this.showSuggestions = false;
        return;
      }

      // Check if query exists in the full list (cache) before making API calls
      try {
        if (this.countryBuyingIn.TwoCharCode == "US") {
          this.filteredRegions = [];
          this.filteredRegions = this.usaRegions;
          this.showSuggestions = true;
          return;
        }
        const response = await axios.get(
          process.env.VUE_APP_REGIONS_API_URL +
            `/LocationHierarchy/${this.countryBuyingIn.TwoCharCode}/`,
          {
            params: { searchTerm: this.query, limit: 5 },
          },
        );
        this.regions = response.data.result; // Store the full list
        this.filteredRegions = this.regions;
        const undecided = {
          geoNameId: 0,
          displayName: "-- Undecided --",
          parentGeoNameId: 0,
          parentDisplayName: "",
          level: 1,
          population: 0,
          levelName: "",
        };
        //always add undecided option at the bottom
        this.filteredRegions.push(undecided);
        this.showSuggestions = true;
      } catch (error) {
        console.error("Error fetching regions:", error);
        this.errorMessage = "Unable to load regions. Please try again.";
      }
    },

    // Select a region
    selectRegion(region) {
      this.query = region.displayName;
      this.selectedRegion = region;
      this.regionId = region.geoNameId;
      this.region = region.displayName;
      this.showSuggestions = false;
      this.regionSelectionError = false;
      this.errorMessage = "";
      console.log("Selected region:", region);
    },
    highlightMatch(text) {
      const regex = new RegExp(`(${this.query})`, "gi");
      return text.replace(regex, "<strong>$1</strong>");
    },
    validateSelection() {
      if (!this.selectedRegion) {
        this.errorMessage = "Please select a valid region from the list.";
        this.regionSelectionError = true;
        return false;
      }
      return true;
    },
  },
  async created() {
    if (this.skipSmartStep) this.$store.dispatch("setHideNavigation", true);
    else this.$store.dispatch("setHideNavigation", false);
    //show yoh logo
    this.$store.dispatch("showYOHLogo", true);
    // Initialize debounced function for API calls
    this.debouncedSearch = _.debounce(this.fetchRegions, 200);
  },
};
</script>
<style scoped>
.c-orange {
  color: #ff6600;
}
.dark-gray {
  color: #767676;
}
.suggestion-box {
  display: block;
  position: absolute;
  z-index: 1000;
  background-color: white;
  border: 1px solid #ddd;
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  padding: 0;
  margin: 0;
  list-style: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.suggestion-box li {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  font-size: 16px;
}

.suggestion-box li:last-child {
  border-bottom: none;
}

.suggestion-box li:hover {
  background-color: #f8f9fa;
}
@media (max-width: 768px) {
  .suggestion-box {
    font-size: 14px;
    max-height: 150px;
  }

  .suggestion-box li {
    padding: 10px 14px;
  }
}
</style>
